import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import {
  createFinanceRule,
  deleteFinanceRule,
  getFinanceRules,
  getFinanceTransactions,
  previewFinanceRule,
  updateFinanceRule,
} from "../../utils/apiClient";
import AccountSelect from "./AccountSelect";
import FinanceTransactionList from "./FinanceTransactionList";

function isoDate(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function last30Days() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from: isoDate(from), to: isoDate(to) };
}

const TOTAL_ACCOUNT = "total";

function flattenCategories(tree, prefix = "") {
  const out = [];
  (tree || []).forEach((node) => {
    const label = prefix ? `${prefix} / ${node.name}` : node.name;
    out.push({ ...node, label });
    (node.children || []).forEach((child) => {
      out.push({ ...child, label: `${label} / ${child.name}` });
    });
  });
  return out;
}

function clampScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 50;
  return Math.max(1, Math.min(100, Math.round(n)));
}

function emptyForm() {
  return {
    pattern: "",
    label: "",
    category: "",
    ignoreCase: true,
    score: 50,
    account: "",
  };
}

export default function FinanceRulesPanel({ accounts = [], categories, rules, onRulesChange, onError, formatMoney }) {
  const [form, setForm] = useState(emptyForm);
  const [editingRule, setEditingRule] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uncategorizedOnly, setUncategorizedOnly] = useState(false);
  const initialRange = useMemo(() => last30Days(), []);
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo, setDateTo] = useState(initialRange.to);
  const [previewAccount, setPreviewAccount] = useState(TOTAL_ACCOUNT);
  const flatCategories = useMemo(
    () => flattenCategories(categories).filter((cat) => !cat.system_key),
    [categories]
  );
  const showAccountColumn = (accounts || []).length > 1;

  const loadTransactions = useCallback(async () => {
    const params = {
      date_from: dateFrom,
      date_to: dateTo,
    };
    if (previewAccount && previewAccount !== TOTAL_ACCOUNT) {
      params.account_uuid = previewAccount;
    }
    const rows = await getFinanceTransactions(params);
    setTransactions(Array.isArray(rows) ? rows : []);
  }, [dateFrom, dateTo, previewAccount]);

  useEffect(() => {
    loadTransactions().catch((err) => {
      const detail = err.response?.data?.detail;
      onError(typeof detail === "string" ? detail : err.message || "Kunne ikke hente posteringer");
    });
  }, [loadTransactions, onError, rules]);

  const onDateFromChange = (value) => {
    setDateFrom(value);
    setPreview(null);
    if (value && dateTo && value > dateTo) setDateTo(value);
  };

  const onDateToChange = (value) => {
    setDateTo(value);
    setPreview(null);
    if (value && dateFrom && value < dateFrom) setDateFrom(value);
  };

  const onPreviewAccountChange = (value) => {
    setPreviewAccount(value);
    setPreview(null);
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setPreview(null);
  };

  const resetForm = () => {
    setEditingRule(null);
    setForm(emptyForm());
    setPreview(null);
  };

  const selectRule = (rule) => {
    setEditingRule(rule.rule_uuid);
    setForm({
      pattern: rule.pattern,
      label: rule.label || "",
      category: rule.category_uuid,
      ignoreCase: rule.ignore_case,
      score: rule.score ?? 50,
      account: rule.account_uuid || "",
    });
    setPreview(null);
  };

  const runPreview = async () => {
    if (!form.pattern.trim()) return;
    setPreviewing(true);
    onError("");
    try {
      const data = await previewFinanceRule({
        pattern: form.pattern,
        ignore_case: form.ignoreCase,
        score: clampScore(form.score),
        account_uuid:
          previewAccount && previewAccount !== TOTAL_ACCOUNT
            ? previewAccount
            : null,
        exclude_rule_uuid: editingRule || undefined,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setPreview(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      onError(typeof detail === "string" ? detail : err.message || "Ugyldigt mønster");
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const saveRule = async () => {
    if (!form.pattern.trim() || !form.category) return;
    setSaving(true);
    onError("");
    try {
      const payload = {
        pattern: form.pattern.trim(),
        ignore_case: form.ignoreCase,
        category_uuid: form.category,
        label: form.label.trim() || null,
        score: clampScore(form.score),
        account_uuid: form.account || null,
      };
      if (editingRule) {
        await updateFinanceRule(editingRule, payload);
      } else {
        await createFinanceRule(payload);
      }
      resetForm();
      const next = await getFinanceRules();
      onRulesChange(Array.isArray(next) ? next : []);
      await loadTransactions();
    } catch (err) {
      const detail = err.response?.data?.detail;
      onError(typeof detail === "string" ? detail : err.message || "Kunne ikke gemme regel");
    } finally {
      setSaving(false);
    }
  };

  const removeRule = async (event, ruleUuid) => {
    event.stopPropagation();
    onError("");
    try {
      await deleteFinanceRule(ruleUuid);
      if (editingRule === ruleUuid) resetForm();
      const next = await getFinanceRules();
      onRulesChange(Array.isArray(next) ? next : []);
      await loadTransactions();
    } catch (err) {
      const detail = err.response?.data?.detail;
      onError(typeof detail === "string" ? detail : err.message || "Kunne ikke slette regel");
    }
  };

  const rows = preview
    ? (preview.matches || []).map((row) => ({
        ...row,
        category_name: row.current_category_name,
        extra: row.would_win ? "Vinder" : "Taber",
      }))
    : transactions;

  const visibleRows = uncategorizedOnly
    ? rows.filter((row) => !(row.category_name || row.current_category_name))
    : rows;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            {editingRule ? "Rediger regel" : "Ny regel"}
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              label="Regex"
              value={form.pattern}
              onChange={(e) => setField("pattern", e.target.value)}
              placeholder="netto|rema"
              helperText="Højeste score vinder. Ved ens score vinder den ældste regel."
            />
            <TextField
              label="Navn (valgfrit)"
              value={form.label}
              onChange={(e) => setField("label", e.target.value)}
            />
            <FormControl size="small">
              <InputLabel>Kategori</InputLabel>
              <Select
                label="Kategori"
                value={form.category}
                onChange={(e) => setField("category", e.target.value)}
              >
                {flatCategories.map((cat) => (
                  <MenuItem key={cat.category_uuid} value={cat.category_uuid}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <AccountSelect
              accounts={accounts}
              value={form.account}
              onChange={(value) => setField("account", value)}
              allowEmpty
              emptyLabel="Global"
              label="Gælder for"
            />
            <TextField
              size="small"
              type="number"
              label="Score"
              value={form.score}
              onChange={(e) => setField("score", clampScore(e.target.value))}
              inputProps={{ min: 1, max: 100 }}
              helperText="1–100. Nye regler starter på 50."
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.ignoreCase}
                  onChange={(e) => setField("ignoreCase", e.target.checked)}
                />
              }
              label="Ignorer store/små bogstaver"
            />
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button variant="outlined" onClick={runPreview} disabled={!form.pattern.trim() || previewing}>
                {previewing ? "Viser…" : "Forhåndsvis"}
              </Button>
              <Button
                variant="contained"
                onClick={saveRule}
                disabled={!form.pattern.trim() || !form.category || saving}
              >
                {editingRule ? "Opdater regel" : "Gem regel"}
              </Button>
              {editingRule ? (
                <Button onClick={resetForm}>Ny regel</Button>
              ) : null}
            </Stack>
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            maxHeight: 420,
            overflow: "auto",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, px: 2, py: 1.5 }}>
            Regler
          </Typography>
          {rules.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
              Ingen regler endnu.
            </Typography>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Regel</TableCell>
                  <TableCell>Kategori</TableCell>
                  {showAccountColumn ? <TableCell>Konto</TableCell> : null}
                  <TableCell align="right">Score</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow
                    key={rule.rule_uuid}
                    hover
                    selected={editingRule === rule.rule_uuid}
                    onClick={() => selectRule(rule)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 13 }}>
                      {rule.label ? `${rule.label} · ` : ""}
                      {rule.pattern}
                    </TableCell>
                    <TableCell>
                      {rule.parent_category_name
                        ? `${rule.parent_category_name} / ${rule.category_name}`
                        : rule.category_name}
                    </TableCell>
                    {showAccountColumn ? (
                      <TableCell>{rule.account_name || "Global"}</TableCell>
                    ) : null}
                    <TableCell align="right">{rule.score ?? 50}</TableCell>
                    <TableCell padding="checkbox">
                      <IconButton
                        size="small"
                        aria-label="Slet"
                        onClick={(event) => removeRule(event, rule.rule_uuid)}
                      >
                        <DeleteOutlinedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ borderTop: 1, borderColor: "divider", pt: 1.5 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {preview
            ? `Forhåndsvisning · ${preview.match_count} match`
            : "Posteringer"}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={uncategorizedOnly}
              onChange={(e) => setUncategorizedOnly(e.target.checked)}
            />
          }
          label="Kun ukategoriserede"
        />
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <TextField
          size="small"
          type="date"
          label="Fra"
          value={dateFrom}
          onChange={(e) => onDateFromChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          type="date"
          label="Til"
          value={dateTo}
          onChange={(e) => onDateToChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <AccountSelect
          accounts={accounts}
          value={previewAccount}
          onChange={onPreviewAccountChange}
          showTotal
          sx={{ minWidth: 280, maxWidth: 480 }}
        />
      </Stack>

      <FinanceTransactionList
        rows={visibleRows}
        formatMoney={formatMoney}
        emptyText="Ingen posteringer."
        colorByMatchCount
        showMatchCount
        stickyHeader
        maxHeight={440}
      />
    </Box>
  );
}
